import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, User, LogOut, LayoutDashboard, Shield, Users, BarChart3 } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/utils/api";

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [branding, setBranding] = useState({});

  useEffect(() => {
    const loadBranding = async () => {
      try {
        const res = await api.get("/site-branding");
        setBranding(res.data || {});
      } catch (e) {
        // ignore
      }
    };

    loadBranding();
  }, []);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-sm border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.site_name || "uplino.in"}
                className="h-8 w-auto"
              />
            ) : (
              <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                uplino.in
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                {/* Show Dashboard only for non-agent users */}
                {user.role !== 'agent' && (
                  <Button
                    data-testid="header-dashboard-button"
                    variant="ghost"
                    onClick={() => navigate('/dashboard')}
                    className="text-white hover:bg-white/10"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                )}
                {user.role !== 'agent' && (
                  <Button
                    data-testid="header-analytics-button"
                    variant="ghost"
                    onClick={() => navigate('/analytics')}
                    className="text-white hover:bg-white/10"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Analytics
                  </Button>
                )}
                {(user.role === 'super_admin' || user.role === 'sub_admin') && (
                  <Button
                    data-testid="header-admin-button"
                    variant="ghost"
                    onClick={() => navigate('/admin')}
                    className="text-white hover:bg-white/10"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Admin
                  </Button>
                )}
                {(user.role === 'agent' || user.role === 'super_admin') && (
                  <Button
                    data-testid="header-agent-button"
                    variant="ghost"
                    onClick={() => navigate('/agent')}
                    className="text-white hover:bg-white/10"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Agent Panel
                  </Button>
                )}
                <Button
                  data-testid="header-logout-button"
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-white hover:bg-white/10"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  data-testid="header-login-button"
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-white hover:bg-white/10"
                >
                  Login
                </Button>
                <Button
                  data-testid="header-register-button"
                  onClick={() => navigate('/register')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Sign Up
                </Button>
              </>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2">
            {user ? (
              <>
                {/* Show Dashboard only for non-agent users */}
                {user.role !== 'agent' && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      navigate('/dashboard');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-white hover:bg-white/10 justify-start"
                  >
                    Dashboard
                  </Button>
                )}
                {user.role !== 'agent' && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      navigate('/analytics');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-white hover:bg-white/10 justify-start"
                  >
                    Analytics
                  </Button>
                )}
                {/* Show Agent Panel for agents */}
                {(user.role === 'agent' || user.role === 'super_admin') && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      navigate('/agent');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-white hover:bg-white/10 justify-start"
                  >
                    Agent Panel
                  </Button>
                )}
                {/* Show Admin for admins */}
                {(user.role === 'super_admin' || user.role === 'sub_admin') && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      navigate('/admin');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-white hover:bg-white/10 justify-start"
                  >
                    Admin
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-white hover:bg-white/10 justify-start"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-white hover:bg-white/10 justify-start"
                >
                  Login
                </Button>
                <Button
                  onClick={() => {
                    navigate('/register');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white justify-start"
                >
                  Sign Up
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
